/* global window, CustomEvent, Event */

import { makeStyles } from "@fluentui/react-components";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import * as React from "react";
import { apiClient } from "@/api/client";
import { activeTabAtom } from "@/state";
import { tokenCache } from "@/utils/token-cache";
import { officeSelectionKey } from "@/utils/chat-selection";
import { withReportCitationLinks } from "@/utils/report-citations";
import type { CardData } from "@/types";
import type { OfficeSelection } from "@/types/api";
import { performanceNotFoundMessage, type PerformanceQueryResponse } from "@/functions/performance-formula";
import { useAuth } from "@/hooks/useAuth";
import { useOfficeConfiguration } from "@/hooks/useOfficeConfiguration";
import AppHeader from "./layout/app-header";
import TabHeader from "./layout/tab-header";
import { SignedOut } from "./auth/signed-out";
import { SigningIn } from "./auth/signing-in";
import ChatTab from "./tabs/chat-tab";
import HomeTab from "./tabs/home-tab";
import { UserNotFound } from "./auth/user-not-found";
import RequestSelection from "./tabs/chat-tab/request-selection";

apiClient.setTokenProvider(() => tokenCache.getAccessToken());

const App: React.FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [tabSearchQuery, setTabSearchQuery] = React.useState<string>("");

  const { authState, userData } = useAuth();
  const officeConfiguration = useOfficeConfiguration(authState === "signedIn" ? userData : undefined);

  const activeTab = useAtomValue(activeTabAtom);
  const sourceKey = officeSelectionKey(officeConfiguration.officeSelection);
  const [performanceHistory, setPerformanceHistory] = React.useState<{ key: string; answers: CardData[] }>({
    key: "",
    answers: [],
  });
  const historyKey = `${officeConfiguration.userId}:${JSON.stringify(officeConfiguration.selection)}`;
  React.useLayoutEffect(() => {
    setPerformanceHistory({ key: historyKey, answers: [] });
  }, [historyKey]);

  React.useEffect(() => {
    const handleFormulaAnswered = (event: Event) => {
      const detail = (
        event as CustomEvent<
          OfficeSelection &
            Partial<PerformanceQueryResponse> & {
              questionId: string;
              question: string;
              answer: string | null;
            }
        >
      ).detail;
      if (!detail?.context || !sourceKey || officeSelectionKey(detail) !== sourceKey) return;
      if (detail.context.requestType === "due-diligence") {
        queryClient.invalidateQueries({
          queryKey: [
            "queries",
            {
              organizationId: detail.organizationId,
              productId: detail.context.productId,
              requestId: detail.context.requestId,
            },
          ],
        });
        return;
      }
      const card: CardData = {
        id: detail.questionId,
        question: detail.question,
        answer:
          detail.answer ?? (detail.performance ? performanceNotFoundMessage(detail.performance) : "No answer found"),
        citations: {
          documentCitations: [],
          qaBankCitations: [],
          reportCitations: withReportCitationLinks(detail.reportCitations ?? [], detail),
        },
      };
      setPerformanceHistory((previous) => ({
        key: historyKey,
        answers: [...(previous.key === historyKey ? previous.answers.filter((item) => item.id !== card.id) : []), card],
      }));
    };
    window.addEventListener("AiirFormulaAnswered", handleFormulaAnswered);
    return () => window.removeEventListener("AiirFormulaAnswered", handleFormulaAnswered);
  }, [sourceKey, historyKey, queryClient]);

  switch (authState) {
    case "initializing":
      return (
        <div className={styles.root}>
          <SigningIn />
        </div>
      );
    case "signedOut":
      return (
        <div className={styles.root}>
          <SignedOut />
        </div>
      );
    case "signingIn":
      return (
        <div className={styles.root}>
          <SigningIn />
        </div>
      );
    case "error":
      return (
        <div className={styles.root}>
          <UserNotFound />
        </div>
      );
    case "signedIn":
      return (
        <div className={styles.root}>
          <AppHeader user={userData} showTabs={true} />
          <TabHeader searchQuery={tabSearchQuery} onSearchChange={setTabSearchQuery} />
          <div className={styles.content}>
            <RequestSelection configuration={officeConfiguration}>
              {activeTab === "home" && (
                <HomeTab
                  searchQuery={tabSearchQuery}
                  selection={officeConfiguration.officeSelection}
                  performanceAnswers={performanceHistory.key === historyKey ? performanceHistory.answers : []}
                />
              )}
              {activeTab === "chat" && <ChatTab selection={officeConfiguration.officeSelection} />}
            </RequestSelection>
          </div>
        </div>
      );
  }
};

export default App;

const useStyles = makeStyles({
  root: {
    height: "100vh",
    maxHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  content: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto", padding: "16px" },
});
